/**
 * Items list route — thin shim mounting `<ItemsListPage />`.
 *
 * The list page owns everything (page-head, KPIs, tabs, table, grid).
 * The route's only job is to provide WorkspaceShell chrome + decode
 * the search params into props.
 *
 * @see apps/web/src/features/items/list/items-list-page.tsx
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { hasPermission } from "@flint/api-contract";
import { ItemsListPage } from "../../../../features/items/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { meQueryOptions } from "../../../../lib/session.ts";

interface ItemsSearch {
  q?: string | undefined;
  tab?: "all" | "goods" | "services" | "low_stock" | "untagged" | "archived" | undefined;
}

const TAB_VALUES = ["all", "goods", "services", "low_stock", "untagged", "archived"] as const;

export const Route = createFileRoute("/(workspace)/sales/items/")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "items.view")) {
      throw notFound();
    }
  },
  component: ItemsListRoute,
  validateSearch: (s: Record<string, unknown>): ItemsSearch => {
    const out: ItemsSearch = {};
    if (typeof s.q === "string" && s.q) out.q = s.q;
    if (s.tab && TAB_VALUES.includes(s.tab as (typeof TAB_VALUES)[number])) {
      out.tab = s.tab as ItemsSearch["tab"];
    }
    return out;
  },
});

function ItemsListRoute() {
  const search = Route.useSearch();
  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Sales", to: "/" }, { label: "Items" }]}
      viewportClassName="p-0"
      contentClassName="max-w-none"
    >
      <ItemsListPage initialTab={search.tab ?? "all"} initialQuery={search.q ?? ""} />
    </WorkspaceShell>
  );
}
